'use memo';

import { Link } from '@tanstack/react-router';
import TrendBadge from '@/components/Badge/TrendBadge';
import { StatCardContent, StatCardHighlight, StatCardPrimaryValue, StatCardSecondaryValue, StatCardTable, StatCardTableHorizBar, StatCardTableRow, StatCardValueContainer } from '@/components/Card/StatCard';
import Spacer from '@/components/Layout/Spacer';
import { Separator } from '@/components/ui/separator';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { OverviewShadowDetectionResponse } from '../-api';
import { BentoCard, SeeMoreButton } from './BentoCard';
import type { ShadowType } from './ShadowTypeBadge';
import { ShadowTypeBadge } from './ShadowTypeBadge';

/** カード2: シャドー検知（今月・IT/ID/AI 内訳＋前月比）。 */
export function ShadowDetectionCard({
  data
}: {
  data: OverviewShadowDetectionResponse | undefined;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.accesses.overview.bento'
  });
  const it = data?.shadowIt.currentMonthCount ?? 0;
  const id = data?.shadowId.currentMonthCount ?? 0;
  const ai = data?.shadowAi.currentMonthCount ?? 0;
  const total = it + id + ai;
  const maxCount = Math.max(1, it, id, ai);
  const rows: {
    key: ShadowType;
    value: number;
    changePercentage: number;
  }[] = [{
    key: 'it',
    value: it,
    changePercentage: data?.shadowIt.changePercentage ?? 0
  }, {
    key: 'id',
    value: id,
    changePercentage: data?.shadowId.changePercentage ?? 0
  }, {
    key: 'ai',
    value: ai,
    changePercentage: data?.shadowAi.changePercentage ?? 0
  }];
  return <BentoCard title={t('shadowDetection.title')} help={t('shadowDetection.help')} headerRight={<SeeMoreButton>
          <Link to="/accesses/alerts" search={{
      alertTab: 'shadow-it'
    }}>
            {t('seeMore')}
          </Link>
        </SeeMoreButton>}>
      <StatCardHighlight className="py-2">
        <StatCardValueContainer>
          <StatCardPrimaryValue>{total.toLocaleString()}</StatCardPrimaryValue>
          <StatCardSecondaryValue>
            {t('shadowDetection.unit')}
          </StatCardSecondaryValue>
        </StatCardValueContainer>
      </StatCardHighlight>
      <Spacer variant="xs" />
      <Separator />
      <Spacer variant="xs" />
      <StatCardContent>
        <StatCardTable>
          {rows.map(row => <StatCardTableRow key={row.key}>
              <ShadowTypeBadge type={row.key} />
              <StatCardTableHorizBar value={row.value} maxValue={maxCount} />
              <TrendBadge value={row.changePercentage / 100} variant="percent" color="neutral" className="ml-auto" />
            </StatCardTableRow>)}
        </StatCardTable>
      </StatCardContent>
    </BentoCard>;
}